import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import CategoryScore from "./CategoryScore";
import ReportCategoryModal from "./ReportCategoryModal";
import ReportDetailsContainer from "../ReportDetailsContainer/ReportDetailsContainer";
import WellnessScoreContainer from "../WellnessScoreContainer/WellnessScoreContainer";
import ReportAlertList from "../ReportAlertList/ReportAlertList";
import ReportInsights from "../ReportInsights/ReportInsights";
import { useConfig } from "../../contexts/ConfigContext";
import { supabase } from "../../supabaseClient";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import { fetchWithAuth } from "../../utils/api";
import "./dashboard.css";

export default function ReportDashboard() {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const { config } = useConfig();
  const API_BASE = config.apiBase;

  // fetch everything when we mount
  useEffect(() => {
    async function fetchData() {
      try {
        // Fetch the event
        const res = await fetchWithAuth(`${API_BASE}/evaluation/${slug}/report`);

        // Handle 401 - if fetchWithAuth failed to refresh
        if (res.status === 401) {
          console.warn("🔐 Token expired or invalid");
          localStorage.removeItem("userRole");
          localStorage.removeItem("userPermissions");
          window.location.href = "/login";
          return;
        }

        const json = await res.json();
        if (!res.ok) throw new Error(json.error || res.statusText);
        setEvent(json.event);

        // Fetch the wellness summary using the event UUID
        const { data, error } = await supabase.rpc("get_event_wellness_summary", {
          event_uuid: json.event.id,
        });
        if (error) {
          console.error("Error calling RPC:", error);
          throw error;
        }
        if (data && Object.keys(data).length > 0) {
          setReportData(data);
        }
      } catch (err) {
        setLoadError(err.message);
      }
    }

    fetchData();
  }, [slug, API_BASE]);

  if (loadError) {
    return <p className="error-text">Error loading report: {loadError}</p>;
  }
  if (!reportData) {
    return <LoadingSpinner message="Loading report…" />;
  }

  const wellnessBg = config.gradeScale[reportData.overall_wellness_grade]?.color || "#f5faf7ff";

  const handleExpand = (category) => {
    setSelectedCategory(category);
    setIsModalOpen((open) => !open);
  };

  return (
    <>
      <main className="web-view">
        <div className="report-dashboard">
          <div className="report-title-area">
            <div className="report-title-section">
              <div className="report-title-text">
                <h2 className="evaluation-dashboard-title">Wellness Report</h2>
                <p>{event.name}</p>
              </div>
            </div>
          </div>

          <div className="report-header">
            <div
              className="wellness-score-section"
              style={{ backgroundColor: wellnessBg }}
            >
              <WellnessScoreContainer
                overallScore={reportData.overall_wellness_score}
                maxScore={config.maxScore}
                gradeLetter={reportData.overall_wellness_grade}
              />
            </div>

            <div className="report-info-section">
              <ReportDetailsContainer
                startDate={event.start_date}
                endDate={event.end_date}
                participants={event.num_participants}
                password={event.password}
                evaluationUrl={event.evaluation_url_full}
              />
            </div>
          </div>

          <div className="category-title-area">
            <h2>Report Categories</h2>
          </div>

          <div className="report-categories-section">
            {reportData.categories.map((category, index) => {
              // You can adjust how you get the scores for each category as needed
              // Only render if the category is included
              return (
                <div key={category.category_id} className={`category-section cat-section-${index + 1}`}>
                  <CategoryScore
                    categoryData={category}
                    onExpand={() => handleExpand(category)}
                  />
                </div>
              );
            })}
          </div>

          <ReportAlertList reportData={reportData} />

          <ReportInsights reportData={reportData} />

          <ReportCategoryModal
            isOpen={isModalOpen}
            closeModal={() => setIsModalOpen(false)}
            category={selectedCategory}
          />
        </div>
      </main>
    </>
  );
}
